import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AdminUserDetail } from "@/components/admin-user-detail";
import { adminAdjustWallet, adminListUsers, adminUpdateUser } from "@/lib/admin.functions";
import type { UserUpdate } from "@/lib/admin.schemas";
import { formatHeat } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/users")({
  head: () =>
    privatePageMetadata("/nc/live/adm/users", "Users | Control room", "Internal administration."),
  component: AdminUsers,
});

type Wallet = "topup" | "earn" | "referral";

function AdminUsers() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const list = useServerFn(adminListUsers);
  const update = useServerFn(adminUpdateUser);
  const adjust = useServerFn(adminAdjustWallet);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["adm-users", search],
    queryFn: () => list({ data: search ? { search } : {} }),
  });

  const save = useMutation({
    mutationFn: (input: UserUpdate) => update({ data: input }),
    onSuccess: () => {
      toast.success("User updated.");
      void qc.invalidateQueries({ queryKey: ["adm-users"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const credit = useMutation({
    mutationFn: (input: { userId: string; wallet: Wallet; heat: number; reason: string }) =>
      adjust({ data: input }),
    onSuccess: () => {
      toast.success("Wallet adjusted.");
      void qc.invalidateQueries({ queryKey: ["adm-users"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Adjustment failed."),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Search name, handle or slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="space-y-2">
        {users.map((u) => {
          const isOpen = open === u.id;
          return (
            <div key={u.id} className="card-surface p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">
                    {u.display_name ?? "Unnamed"}{" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      @{u.handle ?? u.slug ?? "—"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {u.account_mode} · {u.pro_enabled ? "pro active" : "no pro"} ·{" "}
                    {u.roles.join(", ") || "user"} · joined{" "}
                    {new Date(u.created_at).toLocaleDateString()}
                    {u.is_suspended ? " · SUSPENDED" : ""}
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>Top-up {formatHeat(Number(u.wallet?.topup_balance_cents ?? 0))}H</p>
                  <p>Earn {formatHeat(Number(u.wallet?.earn_balance_cents ?? 0))}H</p>
                  <p>Reward {formatHeat(Number(u.wallet?.referral_balance_cents ?? 0))}H</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setOpen(isOpen ? null : u.id)}>
                  {isOpen ? "Close" : "Manage"}
                </Button>
              </div>

              {isOpen && (
                <div className="mt-4 grid gap-4 border-t pt-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Account active</Label>
                      <Switch
                        checked={!u.is_suspended}
                        onCheckedChange={(v) =>
                          save.mutate({ userId: u.id, isSuspended: !v })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Professional profile</Label>
                      <Switch
                        checked={Boolean(u.pro_enabled)}
                        onCheckedChange={(v) => save.mutate({ userId: u.id, proEnabled: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Public profile</Label>
                      <Switch
                        checked={Boolean(u.is_public)}
                        onCheckedChange={(v) => save.mutate({ userId: u.id, isPublic: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Admin role</Label>
                      <Switch
                        checked={u.roles.includes("admin")}
                        onCheckedChange={(v) =>
                          save.mutate(
                            v
                              ? { userId: u.id, role: "admin" }
                              : { userId: u.id, removeRole: "admin" },
                          )
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`note-${u.id}`}>Internal note</Label>
                      <Textarea
                        id={`note-${u.id}`}
                        defaultValue={u.admin_note ?? ""}
                        onBlur={(e) =>
                          save.mutate({ userId: u.id, adminNote: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-semibold">Adjust wallets (Heat)</p>
                    {(["topup", "earn", "referral"] as const).map((w) => (
                      <WalletRow
                        key={w}
                        wallet={w}
                        onApply={(heat, reason) =>
                          credit.mutate({ userId: u.id, wallet: w, heat, reason })
                        }
                      />
                    ))}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor={`slug-${u.id}`}>Public slug</Label>
                        <Input
                          id={`slug-${u.id}`}
                          defaultValue={u.slug ?? ""}
                          onBlur={(e) => save.mutate({ userId: u.id, slug: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`handle-${u.id}`}>Handle</Label>
                        <Input
                          id={`handle-${u.id}`}
                          defaultValue={u.handle ?? ""}
                          onBlur={(e) => save.mutate({ userId: u.id, handle: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isOpen && (
                <div className="mt-4 border-t pt-4">
                  <AdminUserDetail userId={u.id} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WalletRow({
  wallet,
  onApply,
}: {
  wallet: Wallet;
  onApply: (heat: number, reason: string) => void;
}) {
  const [heat, setHeat] = useState("");
  const [reason, setReason] = useState("admin_adjustment");
  return (
    <div className="flex items-end gap-2">
      <div className="w-24">
        <Label className="text-xs capitalize">{wallet}</Label>
        <Input
          value={heat}
          placeholder="±Heat"
          onChange={(e) => setHeat(e.target.value.replace(/[^\d.-]/g, ""))}
        />
      </div>
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="flex-1"
        placeholder="Reason"
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          const n = Number(heat);
          if (!n) return;
          onApply(n, reason || "admin_adjustment");
          setHeat("");
        }}
      >
        Apply
      </Button>
    </div>
  );
}
